/**
 * useReleaseTimeline Hook
 *
 * @module features/releases/hooks/useReleaseTimeline
 * @description Fetches timeline entries (commits and runs) for a property
 * using infinite query for cursor-based pagination.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { throwReleaseApiError } from '../releaseApiError';
import type { ReleaseQARunStatus, ReleaseQATriggerSource } from '../releaseTypes';

export type TimelineSuiteRun = {
  executionId: string;
  status:
    | 'queued'
    | 'running'
    | 'completed'
    | 'completed_with_errors'
    | 'completed_with_inconclusive'
    | 'canceled'
    | 'timeout';
  totalWorkflows: number;
  completedWorkflows: number;
  successfulWorkflows: number;
  failedWorkflows: number;
  inconclusiveWorkflows: number;
  skippedWorkflows: number;
  degradedWorkflows: number;
};

export type TimelineCommitEntry = {
  type: 'commit';
  id: string;
  sha: string;
  shortSha: string;
  message: string;
  authorName: string | null;
  authorEmail: string | null;
  authorAvatarUrl: string | null;
  commitUrl: string | null;
  committedAt: string;
  /** Covered by a release run — at or before this repo's checkpoint. */
  isTested: boolean;
  /**
   * Source repo for the commit (multi-repo configs). Older rows pre-backfill
   * may have these null — the timeline groups them into the primary column.
   */
  propertyGithubRepoId: string | null;
  repoFullName: string | null;
  suiteRun: TimelineSuiteRun | null;
  tasks: Array<{
    id: string;
    title: string;
    resolutionPrNumber: number | null;
    completedAt: string | null;
  }>;
};

export type TimelineRunEntry = {
  type: 'release_run';
  id: string;
  title: string | null;
  status: ReleaseQARunStatus;
  triggerSource: ReleaseQATriggerSource;
  issuesFound: number;
  /**
   * The commit this run tested up to. The timeline pairs a run with its
   * released commit by matching this — never by position in the merged list,
   * which can name a different repo's commit in a multi-repo config.
   */
  toSha: string | null;
  /** Per-repo `toSha` for multi-repo configs. */
  repoCheckpoints: Array<{ propertyGithubRepoId: string; toSha: string }>;
  commitsAnalyzed: number | null;
  regressionTestsTotal: number;
  regressionTestsPassed: number;
  regressionTestsFailed: number;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};

/**
 * A main build of a pushed-image application: a push made with the tag `main`.
 * `tags` are the tags it was pushed with, whatever has moved since.
 */
export type TimelineBuildEntry = {
  type: 'build';
  /** The push id: the version a claim names. */
  id: string;
  tags: string[];
  pushStatus: 'uploading' | 'importing' | 'ready' | 'failed';
  /** Why the push failed; null unless `pushStatus` is `failed`. */
  error: string | null;
  /** Whether its blobs are kept from expiring. */
  retained: boolean;
  createdAt: string;
  /** The newest workflow suite execution that ran against this push. */
  suiteRun: TimelineSuiteRun | null;
  executionId: string | null;
};

export type TimelineEntry = TimelineCommitEntry | TimelineRunEntry | TimelineBuildEntry;

type TimelineResponse = {
  ok: boolean;
  entries: TimelineEntry[];
  nextCursor: string | null;
};

/**
 * Fetch timeline entries for a property with infinite scrolling.
 */
export function useReleaseTimeline(propertyId: string, options?: { limit?: number }) {
  const limit = options?.limit ?? 50;

  return useInfiniteQuery({
    queryKey: ['release-qa-timeline', propertyId, limit],
    queryFn: async ({ pageParam }): Promise<TimelineResponse> => {
      const params = new URLSearchParams({
        limit: String(limit),
      });
      if (pageParam) {
        params.set('cursor', pageParam);
      }

      const response = await fetch(
        `${API_URL}/release-qa/properties/${propertyId}/timeline?${params}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to fetch timeline');
      }

      return response.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!propertyId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Helper to check if a timeline entry is a commit
 */
export function isCommitEntry(entry: TimelineEntry): entry is TimelineCommitEntry {
  return entry.type === 'commit';
}

/**
 * Helper to check if a timeline entry is a release run
 */
export function isRunEntry(entry: TimelineEntry): entry is TimelineRunEntry {
  return entry.type === 'release_run';
}

/**
 * Helper to check if a timeline entry is a build of a pushed-image application
 */
export function isBuildEntry(entry: TimelineEntry): entry is TimelineBuildEntry {
  return entry.type === 'build';
}
